import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApplicationTrackingPanel = ({ applications, onViewDetails, onAddApplication }) => {
  const [filterStatus, setFilterStatus] = useState('all');

  // Calculate statistics
  const totalApplications = applications?.length || 0;
  const acceptances = applications?.filter(app => app?.status === 'accepted') || [];
  const rejections = applications?.filter(app => app?.status === 'rejected') || [];
  const pending = applications?.filter(app => app?.status === 'pending') || [];

  // Group rejections by reason
  const rejectionReasons = rejections?.reduce((acc, app) => {
    const reason = app?.rejectionReason || 'Unbekannt';
    acc[reason] = (acc?.[reason] || 0) + 1;
    return acc;
  }, {});

  // Filter applications based on status
  const filteredApplications = filterStatus === 'all' 
    ? applications 
    : applications?.filter(app => app?.status === filterStatus);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-success/10 text-success border-success/20';
      case 'rejected':
        return 'bg-error/10 text-error border-error/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'accepted':
        return 'Zusage';
      case 'rejected':
        return 'Absage';
      case 'pending':
        return 'In Bearbeitung';
      default:
        return 'Unbekannt';
    }
  };

  const getRejectionReasonLabel = (reason) => {
    const reasons = {
      'qualification_mismatch': 'Qualifikation passt nicht',
      'no_interest_shown': 'Keine Interesse gezeigt',
      'no_show_interview': 'Nicht zum Termin erschienen',
      'other': 'Sonstige Gründe'
    };
    return reasons?.[reason] || reason;
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Applications */}
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-6 border border-primary/20">
          <div className="flex items-center justify-between mb-2">
            <Icon name="FileText" size={24} className="text-primary" />
            <div className="text-right">
              <p className="text-3xl font-bold text-foreground">{totalApplications}</p>
            </div>
          </div>
          <p className="text-sm font-medium text-muted-foreground">Gesamt Bewerbungen</p>
        </div>

        {/* Acceptances */}
        <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-lg p-6 border border-success/20">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle" size={24} className="text-success" />
            <div className="text-right">
              <p className="text-3xl font-bold text-foreground">{acceptances?.length}</p>
            </div>
          </div>
          <p className="text-sm font-medium text-muted-foreground">Zusagen</p>
        </div>

        {/* Rejections */}
        <div className="bg-gradient-to-br from-error/10 to-error/5 rounded-lg p-6 border border-error/20">
          <div className="flex items-center justify-between mb-2">
            <Icon name="XCircle" size={24} className="text-error" />
            <div className="text-right">
              <p className="text-3xl font-bold text-foreground">{rejections?.length}</p>
            </div>
          </div>
          <p className="text-sm font-medium text-muted-foreground">Absagen</p>
        </div>

        {/* Pending */}
        <div className="bg-gradient-to-br from-warning/10 to-warning/5 rounded-lg p-6 border border-warning/20">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={24} className="text-warning" />
            <div className="text-right">
              <p className="text-3xl font-bold text-foreground">{pending?.length}</p>
            </div>
          </div>
          <p className="text-sm font-medium text-muted-foreground">In Bearbeitung</p>
        </div>
      </div>

      {/* Rejection Reasons Analysis */}
      {rejections?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="PieChart" size={20} />
            Absagegründe Analyse
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(rejectionReasons)?.map(([reason, count]) => (
              <div key={reason} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                    <Icon name="AlertCircle" size={18} className="text-error" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{getRejectionReasonLabel(reason)}</p>
                    <p className="text-xs text-muted-foreground">{count} {count === 1 ? 'Absage' : 'Absagen'}</p>
                  </div>
                </div>
                <div className="text-2xl font-bold text-error">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('all')}
          >
            Alle ({totalApplications})
          </Button>
          <Button
            variant={filterStatus === 'accepted' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('accepted')}
          >
            Zusagen ({acceptances?.length})
          </Button>
          <Button
            variant={filterStatus === 'rejected' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('rejected')}
          >
            Absagen ({rejections?.length})
          </Button>
          <Button
            variant={filterStatus === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('pending')}
          >
            In Bearbeitung ({pending?.length})
          </Button>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={onAddApplication}
        >
          Neue Bewerbung
        </Button>
      </div>

      {/* Applications List */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Firma
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Bewerbungsdatum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Antwortdatum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Grund
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredApplications?.length > 0 ? (
                filteredApplications?.map((application) => (
                  <tr key={application?.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="Building" size={18} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{application?.company}</p>
                          <p className="text-xs text-muted-foreground">{application?.companyLocation}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-foreground">{application?.position}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-foreground">{application?.applicationDate}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(application?.status)}`}>
                        {application?.status === 'accepted' && <Icon name="CheckCircle" size={14} />}
                        {application?.status === 'rejected' && <Icon name="XCircle" size={14} />}
                        {application?.status === 'pending' && <Icon name="Clock" size={14} />}
                        {getStatusLabel(application?.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-foreground">{application?.responseDate || '-'}</p>
                    </td>
                    <td className="px-6 py-4">
                      {application?.rejectionReason ? (
                        <p className="text-sm text-muted-foreground">{getRejectionReasonLabel(application?.rejectionReason)}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground">-</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onViewDetails?.(application?.id)}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Icon name="Inbox" size={48} className="text-muted-foreground/50" />
                      <p className="text-muted-foreground">Keine Bewerbungen gefunden</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Companies with Acceptances */}
      {acceptances?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Award" size={20} className="text-success" />
            Firmen mit Zusagen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {acceptances?.map((app) => (
              <div key={app?.id} className="flex items-center gap-3 p-4 bg-success/5 rounded-lg border border-success/20">
                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                  <Icon name="Building" size={20} className="text-success" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{app?.company}</p>
                  <p className="text-xs text-muted-foreground">{app?.position}</p>
                  <p className="text-xs text-success mt-1">Zusage am {app?.responseDate}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Companies with Rejections */}
      {rejections?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="AlertCircle" size={20} className="text-error" />
            Firmen mit Absagen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rejections?.map((app) => (
              <div key={app?.id} className="flex items-center gap-3 p-4 bg-error/5 rounded-lg border border-error/20">
                <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                  <Icon name="Building" size={20} className="text-error" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{app?.company}</p>
                  <p className="text-xs text-muted-foreground">{app?.position}</p>
                  <p className="text-xs text-error mt-1">{getRejectionReasonLabel(app?.rejectionReason)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationTrackingPanel;